import { t, onLang } from './i18n.js';
import { commandBar } from './CommandBar.js';

/**
 * Create or join a room.
 *
 * ## The code is the product
 *
 * Everything else on this screen is scaffolding around four characters that one
 * person reads out and another types in. So the code is the largest thing here, it
 * is selectable, there is a button that copies the whole link, and the link
 * carries the code as `?room=` so the common case — pasting it into a chat — needs
 * nobody to type anything at all.
 *
 * The alphabet the Worker draws from has no vowels and no 0/O/1/I/L, so a code
 * cannot be misheard into a different room and cannot spell a word. That decision
 * lives in the Worker; this screen just has to not undo it, which is why the input
 * upper-cases and strips as you type rather than validating on submit.
 */

export const TUNING = {
  openKey: 'KeyP',
  storeName: 'ob.name',
};

function savedName() {
  try {
    return localStorage.getItem(TUNING.storeName) || '';
  } catch {
    return '';
  }
}

function saveName(v) {
  try {
    localStorage.setItem(TUNING.storeName, v);
  } catch {
    /* private browsing */
  }
}

/** A default that is not "PLAYER" for everybody in the room. */
function defaultName() {
  return savedName() || `PLAYER-${Math.floor(Math.random() * 900 + 100)}`;
}

export class Multiplayer {
  constructor(ctx) {
    this.ctx = ctx;
    this.open = false;
    this.busy = false;
    this.error = null;
    this.state = { active: false, host: false, code: null, peers: [] };
    this.name = defaultName();

    this._build();
    this._bind();
    this._offLang = onLang(() => this.open && this._render());

    ctx.events.on('net:state', (s) => {
      this.state = s;
      if (this.open) this._render();
    });
    ctx.events.on('net:closed', ({ reason }) => {
      this.error = reason;
      if (this.open) this._render();
    });

    // A link with a room in it should join it, not make the player find this
    // screen and type what they were already sent.
    const q = new URLSearchParams(location.search).get('room');
    if (q) {
      this.show();
      this.codeInput.value = q.toUpperCase();
      this._join();
    }
  }

  _build() {
    const host = document.getElementById('ui-root') ?? document.body;
    const root = document.createElement('div');
    root.className = 'mp-root';
    root.hidden = true;
    root.innerHTML = `
      <div class="mp-panel">
        <div class="mp-head">
          <h2 class="mp-title"></h2>
          <p class="mp-sub"></p>
        </div>
        <div class="mp-body">
          <label class="mp-field">
            <span class="mp-label" data-f="nameLabel"></span>
            <input class="mp-input mp-name" maxlength="16" autocomplete="off" />
          </label>

          <div class="mp-sec mp-sec-idle">
            <button class="mp-big" data-act="host"></button>
            <div class="mp-or" data-f="or"></div>
            <div class="mp-joinrow">
              <input class="mp-input mp-code" maxlength="6" autocomplete="off"
                     inputmode="latin" spellcheck="false" />
              <button class="mp-go" data-act="join"></button>
            </div>
          </div>

          <div class="mp-sec mp-sec-live" hidden>
            <div class="mp-codebox">
              <div class="mp-codelabel" data-f="codeLabel"></div>
              <div class="mp-code" data-f="code"></div>
              <button class="mp-copy" data-act="copy"></button>
            </div>
            <div class="mp-peers"></div>
            <button class="mp-leave" data-act="leave"></button>
          </div>

          <p class="mp-note" data-f="note"></p>
          <p class="mp-error" hidden></p>
        </div>
        <div class="mp-foot"></div>
      </div>`;
    host.appendChild(root);
    this.root = root;
    this.nameInput = root.querySelector('.mp-name');
    this.codeInput = root.querySelector('.mp-code');
    this.foot = root.querySelector('.mp-foot');
  }

  _bind() {
    this._onKey = (e) => {
      if (e.code === TUNING.openKey && !this.open && !e.repeat) {
        e.preventDefault();
        e.stopPropagation();
        this.show();
      } else if (this.open && e.code === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
      }
    };
    document.addEventListener('keydown', this._onKey, true);

    // The panel owns its own typing: without this, WASD in the name field also
    // walks the player around behind the menu.
    this.root.addEventListener('keydown', (e) => e.stopPropagation());

    this.root.addEventListener('click', (e) => {
      const act = e.target.closest('[data-act]')?.dataset.act;
      if (act === 'host') this._host();
      else if (act === 'join') this._join();
      else if (act === 'copy') this._copy();
      else if (act === 'leave') this.ctx.net?.leave('you left');
      else if (act === 'close') this.hide();
    });

    this.nameInput.addEventListener('input', () => {
      this.name = this.nameInput.value.trim().slice(0, 16) || defaultName();
      saveName(this.name);
    });
    // Upper-case and strip as they type, so what is on screen is what will be sent.
    this.codeInput.addEventListener('input', () => {
      const v = this.codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (v !== this.codeInput.value) this.codeInput.value = v;
    });
  }

  async _host() {
    if (this.busy) return;
    this.busy = true;
    this.error = null;
    this._render();
    try {
      await this.ctx.net.host(this.name);
    } catch (err) {
      this.error = String(err.message ?? err);
    }
    this.busy = false;
    this._render();
  }

  async _join() {
    if (this.busy) return;
    const code = this.codeInput.value.trim();
    if (code.length < 3) {
      this.error = t('mp.badCode');
      this._render();
      return;
    }
    this.busy = true;
    this.error = null;
    this._render();
    try {
      await this.ctx.net.join(code, this.name);
    } catch (err) {
      this.error = String(err.message ?? err);
    }
    this.busy = false;
    this._render();
  }

  _shareLink() {
    const u = new URL(location.href);
    u.searchParams.set('room', this.state.code ?? '');
    return u.toString();
  }

  async _copy() {
    const link = this._shareLink();
    try {
      await navigator.clipboard.writeText(link);
      this.root.querySelector('.mp-copy').textContent = t('mp.copied');
      setTimeout(() => this._render(), 1400);
    } catch {
      /**
       * Clipboard access needs a secure context and, on some browsers, a gesture
       * it does not think it got. Falling back to selecting the text is not a
       * consolation prize — it is the thing the player then does by hand anyway,
       * done for them.
       */
      const el = this.root.querySelector('[data-f="code"]');
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  show() {
    this.open = true;
    this.root.hidden = false;
    this.nameInput.value = this.name;
    this._render();
    this.ctx.events.emit('ui:screen', { id: 'multiplayer', open: true });
  }

  hide() {
    this.open = false;
    this.root.hidden = true;
    this.ctx.events.emit('ui:screen', { id: 'multiplayer', open: false });
  }

  _render() {
    const r = this.root;
    const s = this.state;
    const live = !!s.active;

    r.querySelector('.mp-title').textContent = t('mp.title');
    r.querySelector('.mp-sub').textContent = t('mp.sub');
    r.querySelector('[data-f="nameLabel"]').textContent = t('mp.name');
    r.querySelector('[data-f="or"]').textContent = t('mp.or');
    r.querySelector('[data-f="codeLabel"]').textContent = t('mp.roomCode');
    r.querySelector('[data-f="note"]').textContent = t('mp.note');
    r.querySelector('[data-act="host"]').textContent = this.busy ? t('mp.working') : t('mp.createRoom');
    r.querySelector('[data-act="join"]').textContent = this.busy ? t('mp.working') : t('mp.joinRoom');
    r.querySelector('[data-act="copy"]').textContent = t('mp.copyLink');
    r.querySelector('[data-act="leave"]').textContent = t('mp.leave');
    this.codeInput.placeholder = t('mp.codePlaceholder');

    r.querySelector('.mp-sec-idle').hidden = live;
    r.querySelector('.mp-sec-live').hidden = !live;

    if (live) {
      r.querySelector('[data-f="code"]').textContent = s.code ?? '----';
      const rows = [
        `<div class="mp-peer is-me"><b>${escapeHtml(this.name)}</b><i>${
          s.host ? t('mp.youHost') : t('mp.youClient')
        }</i></div>`,
        ...s.peers.map(
          (p) =>
            `<div class="mp-peer${p.open ? ' is-on' : ''}"><b>${escapeHtml(p.name)}</b><i>${
              p.open ? t('mp.connected') : t('mp.connecting')
            }</i></div>`
        ),
      ];
      r.querySelector('.mp-peers').innerHTML = rows.join('');
    }

    const errEl = r.querySelector('.mp-error');
    errEl.hidden = !this.error;
    errEl.textContent = this.error ?? '';

    this.foot.replaceChildren(
      commandBar([{ key: 'cmd.closeShort', code: 'Escape', pad: 'B', primary: true }], {
        padConnected: !!this.ctx.input?.pad?.connected,
      })
    );
  }

  dispose() {
    document.removeEventListener('keydown', this._onKey, true);
    this._offLang?.();
    this.root.remove();
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
}
